import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { cn } from "../lib/utils";

const teal = "var(--teal)";

const INFO_ITEMS = [
  {
    title: "Sales & demos",
    body: "Book a walkthrough of the platform with your specific use case in mind.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={teal}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.37 2 2 0 0 1 3.57 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.73a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
  {
    title: "Onboarding & setup",
    body: "Already a customer? Get help with your account, setup, or team rollout.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={teal}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    title: "General enquiries",
    body: "Partnership, press, or any other question. We'll get back to you.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={teal}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
];

const ROLES = [
  "Founder / Owner",
  "Operations Head",
  "Sales Manager",
  "Finance / Accounts",
  "Distribution Manager",
  "IT / Systems",
  "Other",
];

const ORDER_RANGES = [
  "Under 100 orders",
  "100 – 300 orders",
  "300 – 800 orders",
  "800 – 2,000 orders",
  "2,000+ orders",
];

const INQUIRY_TOPICS = [
  "I want to see a demo",
  "I'm evaluating Maltora for my team",
  "I'm already a customer — need support",
  "Partnership or integration enquiry",
  "Press / media",
  "Something else",
];

const labelTitle: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 700,
  fontSize: 14,
  marginBottom: 4,
};
const labelBody: React.CSSProperties = {
  fontSize: 13,
  color: "var(--text-muted)",
  lineHeight: 1.5,
};

const GET_IN_TOUCH_ENDPOINT =
  "https://nl3h0ogtd0.execute-api.ap-south-1.amazonaws.com/Stage/get-in-touch";

const ORDER_RANGE_PAYLOAD: Record<string, string> = {
  "Under 100 orders": "100",
  "100 – 300 orders": "100-300",
  "300 – 800 orders": "300-800",
  "800 – 2,000 orders": "800-2000",
  "2,000+ orders": "2000+",
};

export default function ContactPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errorFields, setErrorFields] = useState<Set<string>>(new Set());
  const [responseData, setResponseData] = useState<Record<string, any> | null>(
    null
  );

  // Always scroll to top on mount (instant so layout shifts can't win)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;
    const required = form.querySelectorAll<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >("[required]");
    const next = new Set<string>();
    required.forEach((field) => {
      if (!field.value.trim()) next.add(field.name);
    });
    setErrorFields(next);
    if (next.size > 0) return;

    const data = new FormData(form);
    const getStr = (key: string) => String(data.get(key) ?? "").trim();
    const monthlyOrdersRaw = getStr("companySize");
    const payload = {
      firstName: getStr("firstName"),
      lastName: getStr("lastName"),
      companyName: getStr("company"),
      emailId: getStr("email"),
      phone: getStr("phone"),
      userRole: getStr("role").toUpperCase(),
      monthlyOrders: ORDER_RANGE_PAYLOAD[monthlyOrdersRaw] ?? monthlyOrdersRaw,
      topic: getStr("inquiry"),
      message: getStr("message"),
    };

    try {
      setSubmitting(true);
      setSubmitError(null);
      const res = await fetch(GET_IN_TOUCH_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let errorMessage = `Request failed with status ${res.status}`;
        try {
          const errorBody = await res.json();
          const apiError = errorBody?.error;
          if (apiError) {
            const parts = [apiError.message, apiError.type].filter(
              (p) => typeof p === "string" && p.trim().length > 0
            );
            if (parts.length > 0) {
              errorMessage = parts.join(": ");
            }
          } else if (typeof errorBody?.message === "string") {
            errorMessage = errorBody.message;
          }
        } catch {
          // ignore JSON parse errors and fall back to default message
        }
        throw new Error(errorMessage);
      }
      try {
        const body = await res.json();
        setResponseData(body?.data ?? null);
      } catch {
        setResponseData(null);
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = (name: string) =>
    setErrorFields((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev);
      next.delete(name);
      return next;
    });

  const errStyle = (name: string): React.CSSProperties =>
    errorFields.has(name) ? { borderColor: "#E86A0D" } : {};

  return (
    <Layout>
      <div className="contact-page">
        {/* Hero */}
        <section className="contact-hero" aria-labelledby="contact-title">
          <div className="container contact-hero__inner">
            <div className="contact-hero__copy">
              <span
                style={{
                  display: "block",
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--orange)",
                  marginBottom: 16,
                }}
              >
                GET IN TOUCH
              </span>
              <h1
                id="contact-title"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "clamp(32px,4vw,48px)",
                  fontWeight: 700,
                  color: "var(--text-on-dark)",
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  marginBottom: 20,
                }}
              >
                Let&apos;s talk about{" "}
                <em
                  style={{
                    fontStyle: "italic",
                    color: "var(--orange)",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    lineHeight: "inherit",
                  }}
                >
                  your operation.
                </em>
              </h1>
              <p
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 16,
                  color: "var(--text-on-dark)",
                  lineHeight: 1.65,
                  maxWidth: 480,
                  opacity: 0.85,
                }}
              >
                Tell us about your business and we&apos;ll walk you through how
                Maltora fits into your workflow. No pressure, no sales pitch.
              </p>
            </div>
          </div>
        </section>

        {/* Form section */}
        <section
          className="section contact-form-section"
          aria-label="Contact form"
        >
          <div className="container section-reveal contact-form-section__inner">
            <div className="contact-layout">
              <div>
                <h2
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "clamp(20px,2.4vw,24px)",
                    fontWeight: 700,
                    marginBottom: 12,
                    lineHeight: 1.3,
                  }}
                >
                  We typically respond within one business day.
                </h2>
                <p
                  style={{
                    color: "var(--text-muted)",
                    lineHeight: 1.7,
                    marginBottom: 40,
                  }}
                >
                  Whether you&apos;re evaluating Maltora, have a specific
                  question, or just want to see a live demo, we&apos;re happy to
                  help.
                </p>

                <div>
                  {INFO_ITEMS.map((item) => (
                    <div className="contact-info-item" key={item.title}>
                      <div className="contact-info-icon">{item.icon}</div>
                      <div>
                        <div style={labelTitle}>{item.title}</div>
                        <div style={labelBody}>{item.body}</div>
                      </div>
                    </div>
                  ))}
                  <div className="contact-info-item">
                    <div className="contact-info-icon">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={teal}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <polyline points="2,4 12,13 22,4" />
                      </svg>
                    </div>
                    <div>
                      <div style={labelTitle}>Email us</div>
                      <div style={labelBody}>
                        <a
                          href="mailto:support@maltora.app"
                          style={{ color: teal, textDecoration: "none" }}
                        >
                          support@maltora.app
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-form-card">
                <form
                  id="contactForm"
                  ref={formRef}
                  noValidate
                  onSubmit={handleSubmit}
                  style={{ display: submitted ? "none" : undefined }}
                >
                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label" htmlFor="firstName">
                        First name <span>*</span>
                      </label>
                      <input
                        className="input"
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="Rajesh"
                        required
                        style={errStyle("firstName")}
                        onInput={() => clearError("firstName")}
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label" htmlFor="lastName">
                        Last name <span>*</span>
                      </label>
                      <input
                        className="input"
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Menon"
                        required
                        style={errStyle("lastName")}
                        onInput={() => clearError("lastName")}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label" htmlFor="email">
                        Work email <span>*</span>
                      </label>
                      <input
                        className="input"
                        id="email"
                        name="email"
                        type="email"
                        placeholder="rajesh@yourcompany.com"
                        required
                        style={errStyle("email")}
                        onInput={() => clearError("email")}
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label" htmlFor="phone">
                        Phone
                      </label>
                      <input
                        className="input"
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="company">
                      Company name <span>*</span>
                    </label>
                    <input
                      className="input"
                      id="company"
                      name="company"
                      type="text"
                      placeholder="Your brewery or distribution company"
                      required
                      style={errStyle("company")}
                      onInput={() => clearError("company")}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label" htmlFor="role">
                        Your role <span>*</span>
                      </label>
                      <select
                        className="input"
                        id="role"
                        name="role"
                        required
                        defaultValue=""
                        style={errStyle("role")}
                        onChange={() => clearError("role")}
                      >
                        <option value="" disabled>
                          Select your role
                        </option>
                        {ROLES.map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label" htmlFor="companySize">
                        Monthly orders (approx.)
                      </label>
                      <select
                        className="input"
                        id="companySize"
                        name="companySize"
                        defaultValue=""
                      >
                        <option value="" disabled>
                          Select range
                        </option>
                        {ORDER_RANGES.map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="inquiry">
                      What brings you here? <span>*</span>
                    </label>
                    <select
                      className="input"
                      id="inquiry"
                      name="inquiry"
                      required
                      defaultValue=""
                      style={errStyle("inquiry")}
                      onChange={() => clearError("inquiry")}
                    >
                      <option value="" disabled>
                        Select a topic
                      </option>
                      {INQUIRY_TOPICS.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="message">
                      Tell us more
                    </label>
                    <textarea
                      className="input"
                      id="message"
                      name="message"
                      placeholder="What does your current operation look like? What's the biggest pain you're trying to solve?"
                    />
                  </div>

                  <button
                    type="submit"
                    className="form-submit-btn"
                    disabled={submitting}
                    style={
                      submitting
                        ? { opacity: 0.7, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    {submitting ? "Sending..." : "Send message →"}
                  </button>

                  {submitError && (
                    <p
                      role="alert"
                      style={{
                        fontSize: 13,
                        color: "#E86A0D",
                        textAlign: "center",
                        marginTop: 12,
                      }}
                    >
                      {submitError}
                    </p>
                  )}

                  <p
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      textAlign: "center",
                      marginTop: 16,
                      lineHeight: 1.5,
                    }}
                  >
                    By submitting this form you agree to our{" "}
                    <Link
                      to="/privacy"
                      style={{
                        color: teal,
                        fontSize: "inherit",
                        fontFamily: "inherit",
                        fontWeight: "inherit",
                      }}
                    >
                      Privacy Policy
                    </Link>
                    . We&apos;ll never share your information.
                  </p>
                </form>

                <div
                  className={cn("success-message", submitted && "visible")}
                  id="successMsg"
                >
                  <h3
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 22,
                      fontWeight: 700,
                      marginBottom: 16,
                      textAlign: "center",
                    }}
                  >
                    Thank You for Reaching Out to Maltora
                  </h3>
                  {(() => {
                    const d = responseData ?? {};
                    const firstName = d.firstName ?? "";
                    const lastName = d.lastName ?? "";
                    const fullName =
                      `${firstName} ${lastName}`.trim() || "there";
                    const rows: { label: string; value: string }[] = [
                      {
                        label: "Name",
                        value: `${firstName} ${lastName}`.trim(),
                      },
                      { label: "Company", value: d.companyName ?? "" },
                      { label: "Work Email", value: d.emailId ?? "" },
                      { label: "Phone", value: d.phone ?? "" },
                      { label: "Role", value: d.userRole ?? "" },
                      { label: "Monthly Orders", value: d.monthlyOrders ?? "" },
                      { label: "Topic", value: d.topic ?? "" },
                    ].filter((r) => r.value);

                    return (
                      <div
                        style={{
                          color: "var(--text-body)",
                          lineHeight: 1.7,
                          textAlign: "left",
                          fontSize: 14,
                        }}
                      >
                        <p style={{ marginBottom: 12 }}>Dear {fullName},</p>
                        <p style={{ marginBottom: 12 }}>
                          Thank you for getting in touch with Maltora.
                          We&apos;ve received your enquiry and our team will
                          review the details shared by you shortly. One of our
                          representatives will reach out to you soon to
                          understand your requirements and help you out.
                        </p>
                        {rows.length > 0 && (
                          <>
                            <p style={{ marginBottom: 10 }}>
                              Here&apos;s a quick summary of your submission:
                            </p>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                marginBottom: 16,
                              }}
                            >
                              <tbody>
                                {rows.map((r, i) => (
                                  <tr
                                    key={r.label}
                                    style={{
                                      background:
                                        i % 2 === 0
                                          ? "transparent"
                                          : "rgba(31,95,91,0.06)",
                                    }}
                                  >
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        fontWeight: 600,
                                        width: "40%",
                                        verticalAlign: "top",
                                      }}
                                    >
                                      {r.label}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        color:
                                          r.label === "Topic"
                                            ? teal
                                            : undefined,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {r.value}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        )}
                        <p style={{ marginBottom: 12 }}>
                          We appreciate your interest in Maltora and look
                          forward to connecting with you.
                        </p>
                        <p style={{ marginBottom: 12 }}>
                          For support, reach out to us at{" "}
                          <a
                            href="mailto:support@maltora.app"
                            style={{ color: teal, textDecoration: "underline" }}
                          >
                            support@maltora.app
                          </a>
                          .
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
